// components/DataTable/DataTableEditForm.jsx

import React from "react";
import {
  Form,
  Button,
  Spinner
} from "react-bootstrap";

import {
  findDropdownItem
} from "./utils";

/**
 * @typedef {Object} DataTableEditFormProps
 * @property {Object} row
 * @property {Array<Object>} columns
 * @property {Object} errors
 * @property {function(string, *): void} onChange
 * @property {function(): void} onSave
 * @property {function(): void} onCancel
 * @property {boolean} saving
 * @property {boolean} showButtons
 */

/**
 * @param {DataTableEditFormProps} props
 */
function DataTableEditForm({
  row,
  columns,
  errors,
  onChange,
  onSave,
  onCancel,
  saving,
  showButtons
}) {
  /**
   * @param {Object} column
   * @returns {JSX.Element}
   */
  const renderInput = column => {
    const value = row[column.field];
    const error = errors[column.field];

    /*
     * STRING
     */
    if (column.type === "string") {
      return (
        <Form.Group>
          <Form.Label>
            {column.title}

            {column.required && (
              <span className="text-danger ml-1">
                *
              </span>
            )}
          </Form.Label>

          <Form.Control
            type="text"
            value={
              value === null || value === undefined
                ? ""
                : value
            }
            maxLength={column.maxLength}
            onChange={e =>
              onChange(
                column.field,
                e.target.value
              )
            }
            isInvalid={!!error}
            disabled={saving}
          />

          {error && (
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      );
    }

    /*
     * NUMBER
     */
    if (column.type === "number") {
      return (
        <Form.Group>
          <Form.Label>
            {column.title}

            {column.required && (
              <span className="text-danger ml-1">
                *
              </span>
            )}
          </Form.Label>

          <Form.Control
            type="number"
            value={
              value === null ||
              value === undefined
                ? ""
                : value
            }
            onChange={e => {
              const rawValue = e.target.value;

              /*
               * Empty number input becomes null.
               */
              const newValue =
                rawValue === ""
                  ? null
                  : Number(rawValue);

              onChange(
                column.field,
                newValue
              );
            }}
            isInvalid={!!error}
            disabled={saving}
          />

          {error && (
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      );
    }

    /*
     * BOOL
     */
    if (column.type === "bool") {
      return (
        <Form.Group>
          <Form.Check
            type="checkbox"
            label={column.title}
            checked={value === true}
            onChange={e =>
              onChange(
                column.field,
                e.target.checked
              )
            }
            disabled={saving}
            isInvalid={!!error}
          />

          {error && (
            <div className="invalid-feedback d-block">
              {error}
            </div>
          )}
        </Form.Group>
      );
    }

    /*
     * DROPDOWN
     */
    if (column.type === "dropdown") {
      const dataSource = Array.isArray(
        column.dataSource
      )
        ? column.dataSource
        : [];

      /*
       * Verify selected item.
       */
      findDropdownItem(
        dataSource,
        column,
        value
      );

      return (
        <Form.Group>
          <Form.Label>
            {column.title}

            {column.required && (
              <span className="text-danger ml-1">
                *
              </span>
            )}
          </Form.Label>

          <select className="form-control"
            value={
              value === null ||
              value === undefined
                ? ""
                : String(value)
            }
            onChange={e => {
              const selectedValue =
                e.target.value;

              /*
               * Empty option => null
               */
              if (selectedValue === "") {
                onChange(
                  column.field,
                  null
                );
                return;
              }

              /*
               * Find original object so the
               * original value type is preserved.
               *
               * 2 remains Number 2
               * instead of becoming "2".
               */
              const selectedItem =
                dataSource.find(item => {
                  return (
                    item &&
                    String(
                      item[column.valueField]
                    ) === selectedValue
                  );
                });

              if (selectedItem) {
                onChange(
                  column.field,
                  selectedItem[
                    column.valueField
                  ]
                );
              }
            }}
            isInvalid={!!error}
            disabled={saving}
          >
            {column.placeholder && (
              <option value="">
                {column.placeholder}
              </option>
            )}

            {!column.placeholder && (
              <option value="">
                انتخاب کنید...
              </option>
            )}

            {dataSource.map(item => {
              const itemValue =
                item[column.valueField];

              const itemText =
                item[column.textField];

              return (
                <option
                  key={String(itemValue)}
                  value={String(itemValue)}
                >
                  {itemText}
                </option>
              );
            })}
          </select>

          {error && (
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      );
    }

    return null;
  };

  return (
    <div className="datatable-edit-form">
      {columns.map(column => (
        <div key={column.field}>
          {renderInput(column)}
        </div>
      ))}

      {showButtons && (
        <div className="datatable-edit-buttons">
          <Button
            variant="primary"
            size="sm"
            onClick={onSave}
            disabled={saving}
          >
            {saving && (
              <Spinner
                animation="border"
                size="sm"
                className="mr-1"
              />
            )}

            ذخیره
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={onCancel}
            disabled={saving}
            className="ml-2"
          >
            انصراف
          </Button>
        </div>
      )}
    </div>
  );
}

export default DataTableEditForm;